import { FunctionComponent, useEffect, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import Select from 'react-select'
import { ISelectOption } from 'Frontend/scripts/models/forums.model'
import { ICompetenceArea, ICounty, IMunicipality } from 'Frontend/scripts/models/profile.model'
import {
  getAllCounties,
  getCompetenceAreas,
  getMunicipalitiesByCounty,
} from 'Frontend/scripts/services/profile.service'
import { getUsers } from 'Frontend/scripts/services/users.service'
import { useForm, Controller } from 'react-hook-form'
import { IMembersSearchResults } from 'Frontend/scripts/models/members.model'
import { useTranslation } from 'react-i18next'

interface MembersSearchProps {
  callback: (
    _members: IMembersSearchResults,
    _query: string,
    _county: string,
    _municipality: string,
    _competence: string,
  ) => void | null
}
interface MembersSearchForm {
  query: string
  county: ISelectOption
  municipality: ISelectOption
  competence: ISelectOption
}

const MembersSearch: FunctionComponent<MembersSearchProps> = ({ callback }) => {
  const [counties, setCounties] = useState<ISelectOption[]>([])
  const [municipalities, setMunicipalities] = useState<ISelectOption[]>([])
  const [competences, setCompetences] = useState<ISelectOption[]>([])
  const { t } = useTranslation()
  const { register, handleSubmit, control, setValue, getValues } = useForm<MembersSearchForm>({
    defaultValues: {
      query: '',
      county: {},
      municipality: {},
      competence: {},
    },
  })
  const pageIndex = 1

  const onSubmit = (data: MembersSearchForm) => {
    getUsers(
      pageIndex,
      data.query,
      parseInt(data.county.value),
      parseInt(data.municipality.value),
      parseInt(data.competence.value),
    ).then((members) =>
      callback(members, data.query, data.county.value, data.municipality.value, data.competence.value),
    )
  }

  const getMunicipalities = (countyId: string) => {
    if (countyId != 'all') {
      getMunicipalitiesByCounty(countyId).then((municipalitiesData: IMunicipality[]) => {
        const convertedMunicipalityArray = municipalitiesData?.map((municipality) => ({
          value: municipality.id.toString(),
          label: municipality.name,
        }))
        convertedMunicipalityArray.unshift({ value: 'all', label: t('all-municialities') })
        setMunicipalities(convertedMunicipalityArray)
        setValue('municipality', convertedMunicipalityArray[0])
      })
    } else {
      setMunicipalities([{ value: 'all', label: t('all-municialities') }])
      setValue('municipality', { value: 'all', label: t('all-municialities') })
    }
  }

  const updateMunicipalities = (countyId: string) => {
    getMunicipalities(countyId)
  }

  useEffect(() => {
    getAllCounties().then((countiesData: ICounty[]) => {
      const convertedCountiesArray = countiesData.map((county) => ({
        value: county.id.toString(),
        label: county.name,
      }))
      convertedCountiesArray.unshift({ value: 'all', label: t('all-counties') })
      setCounties(convertedCountiesArray)
      setValue('county', convertedCountiesArray[0])
    })

    getCompetenceAreas().then((competences: ICompetenceArea[]) => {
      const convertedCompetencesArray = competences.map((competence) => ({
        value: competence.id.toString(),
        label: competence.name,
      }))
      convertedCompetencesArray.unshift({ value: 'all', label: t('all-competence-areas') })
      setCompetences(convertedCompetencesArray)
      setValue('competence', convertedCompetencesArray[0])
    })
  }, [])

  useEffect(() => {
    if (counties.length > 0) {
      getMunicipalities(getValues('county.value'))
    }
  }, [counties])

  return (
    <div className="mb-4 forums__search">
      <h2 className="forums__heading">{t('search-person')}</h2>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-2">
        <div className="d-flex gap-3">
          <div className="input-group">
            <Form.Control
              id="inputQuery"
              type="search"
              className="form-control--small"
              placeholder={t('search-word')}
              {...register('query')}
            />
            <Button variant="secondary" type="submit" className="forums__search-button" id="id-search-button">
              <i className="fa fa-search me-2"></i>
              {t('search')}
            </Button>
          </div>
        </div>
        <div className="d-flex flex-column flex-xl-row gap-2">
          <Form.Group className="w-100">
            <Controller
              name="county"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  options={counties}
                  classNamePrefix="single-select"
                  onChange={(value) => {
                    field.onChange(value)
                    value && updateMunicipalities(value.value.toString())
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: '1.6rem',
                    }),
                    option: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: '1.6rem',
                    }),
                  }}
                />
              )}
            />
          </Form.Group>
          <Form.Group className="w-100">
            <Controller
              name="municipality"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  options={municipalities}
                  classNamePrefix="single-select"
                  className="w-100"
                  onChange={(value) => {
                    field.onChange(value)
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: '1.6rem',
                    }),
                    option: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: '1.6rem',
                    }),
                  }}
                />
              )}
            />
          </Form.Group>
          <Form.Group className="w-100">
            <Controller
              name="competence"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  options={competences}
                  classNamePrefix="single-select"
                  className="w-100"
                  onChange={(value) => {
                    field.onChange(value)
                  }}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: '1.6rem',
                    }),
                    option: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: '1.6rem',
                    }),
                  }}
                />
              )}
            />
          </Form.Group>
        </div>
      </Form>
    </div>
  )
}

export default MembersSearch
